import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import {
  Box,
  IconButton,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/joy';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import RichChatInput from '../../components/RichChatInput';
import PolicyMentionPopover from '../../components/PolicyMentionPopover';
import WelcomeScreen from './components/WelcomeScreen';
import TypingIndicator from './components/TypingIndicator';
import ModeSelector from './components/ModeSelector';
import ModelSelector from './components/ModelSelector';
import CropOriginalIcon from '@mui/icons-material/CropOriginal';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { listFiles, uploadFile } from '../../helpers/storage';
import { getDocumentDisplayName } from '../../helpers/documentNames';
import {
  UserMessage,
  AssistantMessage,
  ToolCallGroup,
} from './messages';

const MAX_FILE_SIZE_MB = 50;
const MAX_CHAT_WIDTH = 850;
const CHAT_UPLOADS_FOLDER = 'chat_uploads';

const splitFileName = (fileName = '') => {
  const normalized = fileName.trim();
  if (!normalized) {
    return { baseName: 'document', extension: '' };
  }

  const lastDotIndex = normalized.lastIndexOf('.');
  if (lastDotIndex <= 0) {
    return { baseName: normalized, extension: '' };
  }

  return {
    baseName: normalized.slice(0, lastDotIndex),
    extension: normalized.slice(lastDotIndex),
  };
};

const getStorageItemName = (item) => {
  if (typeof item?.name === 'string' && item.name.trim()) {
    return item.name.trim();
  }

  const itemPath = typeof item?.path === 'string' ? item.path.trim() : '';
  if (!itemPath) return '';

  const pathParts = itemPath.split('/').filter(Boolean);
  return pathParts[pathParts.length - 1] || '';
};

const Chat = ({
  messages,
  busy,
  status,
  message,
  onMessageChange,
  onSend,
  sending,
  onConfirm,
  confirming,
  messagesEndRef,
  isCreationMode = false,
  quotes = [],
  onQuote = () => { },
  onRemoveQuote = () => { },
  agentId = null,
  policies = [],
  policiesLoading = false,
  onLoadPolicies = () => { },
  tasks = [],
  tasksLoading = false,
  hasMoreTasks = false,
  onLoadTasks = () => { },
  onLoadMoreTasks = () => { },
  documents = [],
  documentFolders = [],
  documentsLoading = false,
  documentBreadcrumbs = [],
  onLoadDocuments = () => { },
  onNavigateDocumentFolder = () => { },
  onNavigateDocumentRoot = () => { },
  // Functions props
  functions = [],
  functionsLoading = false,
  onLoadFunctions = () => { },
  // Subscribers props
  subscribers = [],
  subscribersLoading = false,
  onLoadSubscribers = () => { },
  cursorPositionRef = null,
  chatInputRef = null,
  // Model selection props
  models = [],
  modelsLoading = false,
  selectedModelId = null,
  onModelChange = () => { },
  // Mode selection props
  modes = [],
  modesLoading = false,
  selectedMode = 'agent',
  onModeChange = () => { },
  // Document viewer props
  onOpenDocument = () => { },
  documentCropAttachment = null,
  onRemoveCropAttachment = () => { },
  // Policy viewer props
  onOpenPolicy = () => { },
  // Task result viewer props
  onOpenTask = () => { },
  onDeleteChat = null,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingFileName, setUploadingFileName] = useState('');
  const fileInputRef = useRef(null);
  const internalInputRef = useRef(null);
  const latestMessageRef = useRef(message || '');
  const textareaRef = chatInputRef || internalInputRef;

  useEffect(() => {
    latestMessageRef.current = message || '';
  }, [message]);

  // Auto-focus input when creating a new conversation
  useEffect(() => {
    if (isCreationMode && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isCreationMode, textareaRef]);

  // Mention popover state
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionTriggerIndex, setMentionTriggerIndex] = useState(-1);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [mentionMode, setMentionMode] = useState('types');

  const mentionQuery = useMemo(() => {
    if (!mentionOpen || mentionTriggerIndex < 0) return '';
    return message.slice(mentionTriggerIndex + 1);
  }, [mentionOpen, mentionTriggerIndex, message]);

  const filteredPolicies = useMemo(() => {
    if (mentionMode !== 'policies') return [];
    const policyQuery = mentionQuery.toLowerCase().startsWith('policy')
      ? mentionQuery.slice(6).trim()
      : mentionQuery;
    if (!policyQuery) return policies;
    const lowerQuery = policyQuery.toLowerCase();
    return policies.filter(
      (policy) =>
        policy.name?.toLowerCase().includes(lowerQuery) ||
        policy.settings?.type?.toLowerCase().includes(lowerQuery)
    );
  }, [policies, mentionQuery, mentionMode]);

  const filteredTasks = useMemo(() => {
    if (mentionMode !== 'tasks') return [];
    const tasksQuery = mentionQuery.toLowerCase().startsWith('tasks')
      ? mentionQuery.slice(5).trim()
      : mentionQuery;
    if (!tasksQuery) return tasks;
    const lowerQuery = tasksQuery.toLowerCase();
    return tasks.filter(
      (task) =>
        String(task.id).includes(lowerQuery) ||
        task.status?.toLowerCase().includes(lowerQuery) ||
        task.policy_name?.toLowerCase().includes(lowerQuery)
    );
  }, [tasks, mentionQuery, mentionMode]);

  const filteredDocuments = useMemo(() => {
    if (mentionMode !== 'documents') return [];
    const docsQuery = mentionQuery.toLowerCase().startsWith('documents')
      ? mentionQuery.slice(9).trim()
      : mentionQuery;

    const allItems = [
      ...documentFolders.map((f) => ({ ...f, type: 'folder' })),
      ...documents.map((f) => ({
        ...f,
        type: 'file',
        displayName: getDocumentDisplayName(f.name || f.path),
      })),
    ];

    if (!docsQuery) return allItems;
    const lowerQuery = docsQuery.toLowerCase();
    return allItems.filter((item) => {
      const rawName = item.name?.toLowerCase() || '';
      const displayName = item.displayName?.toLowerCase() || '';
      return rawName.includes(lowerQuery) || displayName.includes(lowerQuery);
    });
  }, [documents, documentFolders, mentionQuery, mentionMode]);

  const filteredFunctions = useMemo(() => {
    if (mentionMode !== 'functions') return [];
    const functionsQuery = mentionQuery.toLowerCase().startsWith('functions')
      ? mentionQuery.slice(9).trim()
      : mentionQuery;
    if (!functionsQuery) return functions;
    const lowerQuery = functionsQuery.toLowerCase();
    return functions.filter(
      (func) =>
        func.name?.toLowerCase().includes(lowerQuery) ||
        func.description?.toLowerCase().includes(lowerQuery) ||
        func.function_type?.toLowerCase().includes(lowerQuery)
    );
  }, [functions, mentionQuery, mentionMode]);

  const filteredSubscribers = useMemo(() => {
    if (mentionMode !== 'subscribers') return [];
    const subscribersQuery = mentionQuery.toLowerCase().startsWith('subscribers')
      ? mentionQuery.slice(11).trim()
      : mentionQuery;
    if (!subscribersQuery) return subscribers;
    const lowerQuery = subscribersQuery.toLowerCase();
    return subscribers.filter(
      (sub) =>
        sub.name?.toLowerCase().includes(lowerQuery) ||
        sub.url?.toLowerCase().includes(lowerQuery)
    );
  }, [subscribers, mentionQuery, mentionMode]);

  const mentionTypes = useMemo(() => {
    const types = [
      { id: 'policy', label: 'policy', description: 'Mention a policy' },
      { id: 'tasks', label: 'tasks', description: 'Mention a task' },
      { id: 'documents', label: 'documents', description: 'Mention a document from storage' },
      { id: 'functions', label: 'functions', description: 'Mention a function' },
      { id: 'subscribers', label: 'webhooks', description: 'Mention a webhook' },
    ];
    if (!mentionQuery) return types;
    const lowerQuery = mentionQuery.toLowerCase();
    return types.filter((t) => t.id.includes(lowerQuery) || t.label.includes(lowerQuery));
  }, [mentionQuery]);

  const currentItems = useMemo(() => {
    if (mentionMode === 'types') return mentionTypes;
    if (mentionMode === 'policies') return filteredPolicies;
    if (mentionMode === 'tasks') return filteredTasks;
    if (mentionMode === 'documents') return filteredDocuments;
    if (mentionMode === 'functions') return filteredFunctions;
    if (mentionMode === 'subscribers') return filteredSubscribers;
    return [];
  }, [mentionMode, mentionTypes, filteredPolicies, filteredTasks, filteredDocuments, filteredFunctions, filteredSubscribers]);

  const updateMessage = useCallback((nextMessage) => {
    latestMessageRef.current = nextMessage;
    onMessageChange(nextMessage);
  }, [onMessageChange]);

  const resolveUploadKey = useCallback(async (originalFileName) => {
    const existingNames = new Set();
    let continuationToken = null;

    do {
      const response = await listFiles(CHAT_UPLOADS_FOLDER, continuationToken);
      const data = response?.data || {};
      const files = Array.isArray(data.files) ? data.files : [];

      files.forEach((item) => {
        const itemName = getStorageItemName(item);
        if (itemName) {
          existingNames.add(itemName);
        }
      });

      continuationToken = data.next_continuation_token || null;
    } while (continuationToken);

    const { baseName, extension } = splitFileName(originalFileName);
    let candidateName = `${baseName}${extension}`;
    let counter = 1;

    while (existingNames.has(candidateName)) {
      candidateName = `${baseName} (${counter})${extension}`;
      counter += 1;
    }

    return `${CHAT_UPLOADS_FOLDER}/${candidateName}`;
  }, []);

  const insertDocumentMentionAtCursor = useCallback((filePath) => {
    const currentMessage = latestMessageRef.current || '';
    const rawCursor = typeof cursorPositionRef?.current === 'number'
      ? cursorPositionRef.current
      : currentMessage.length;
    const cursorPos = Math.max(0, Math.min(rawCursor, currentMessage.length));

    const beforeCursor = currentMessage.slice(0, cursorPos);
    const afterCursor = currentMessage.slice(cursorPos);
    const mentionToken = `@document:${filePath}`;

    const needsLeadingSpace = beforeCursor.length > 0 && !/\s$/.test(beforeCursor);
    const needsTrailingSpace = afterCursor.length === 0 || !/^\s/.test(afterCursor);
    const insertion = `${needsLeadingSpace ? ' ' : ''}${mentionToken}${needsTrailingSpace ? ' ' : ''}`;
    const nextMessage = `${beforeCursor}${insertion}${afterCursor}`;

    updateMessage(nextMessage);
    if (cursorPositionRef) {
      cursorPositionRef.current = beforeCursor.length + insertion.length;
    }
  }, [cursorPositionRef, updateMessage]);

  const handleKeyDown = (e) => {
    if (mentionOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < currentItems.length - 1 ? prev + 1 : 0
        );
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : currentItems.length - 1
        );
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        if (currentItems[highlightedIndex]) {
          if (mentionMode === 'types') {
            handleTypeSelect(currentItems[highlightedIndex].id);
          } else if (mentionMode === 'policies') {
            handlePolicySelect(`@policy:${currentItems[highlightedIndex].id}`);
          } else if (mentionMode === 'tasks') {
            handleTaskSelect(`@task:${currentItems[highlightedIndex].id}`);
          } else if (mentionMode === 'documents') {
            const item = currentItems[highlightedIndex];
            if (item.type === 'folder') {
              onNavigateDocumentFolder(item.path);
              setHighlightedIndex(0);
            } else {
              handleDocumentSelect(item);
            }
          } else if (mentionMode === 'functions') {
            handleFunctionSelect(`@function:${currentItems[highlightedIndex].id}`);
          } else if (mentionMode === 'subscribers') {
            handleSubscriberSelect(`@subscriber:${currentItems[highlightedIndex].id}`);
          }
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMentionPopover();
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (isUploading) return;
      onSend();
    }
  };

  const handleTextChange = (newValue, cursorPos) => {
    const safeValue = newValue ?? '';
    const safeCursorPos = typeof cursorPos === 'number' ? cursorPos : safeValue.length;

    if (safeValue.length > message.length && safeCursorPos > 0) {
      const addedChar = safeValue[safeCursorPos - 1];
      if (addedChar === '@') {
        const charBefore = safeCursorPos > 1 ? safeValue[safeCursorPos - 2] : '';
        if (safeCursorPos === 1 || /\s/.test(charBefore)) {
          setMentionOpen(true);
          setMentionTriggerIndex(safeCursorPos - 1);
          setHighlightedIndex(0);
          setMentionMode('types');
        }
      }
    }

    if (mentionOpen && mentionMode === 'types' && mentionTriggerIndex >= 0) {
      const currentQuery = safeValue.slice(mentionTriggerIndex + 1);
      if (currentQuery.toLowerCase() === 'policy') {
        setMentionMode('policies');
        setHighlightedIndex(0);
        onLoadPolicies();
      } else if (currentQuery.toLowerCase() === 'tasks') {
        setMentionMode('tasks');
        setHighlightedIndex(0);
        onLoadTasks();
      } else if (currentQuery.toLowerCase() === 'documents') {
        setMentionMode('documents');
        setHighlightedIndex(0);
        onLoadDocuments();
      } else if (currentQuery.toLowerCase() === 'functions') {
        setMentionMode('functions');
        setHighlightedIndex(0);
        onLoadFunctions();
      } else if (currentQuery.toLowerCase() === 'subscribers') {
        setMentionMode('subscribers');
        setHighlightedIndex(0);
        onLoadSubscribers();
      }
    }

    if (mentionOpen && mentionTriggerIndex >= 0) {
      const textAfterTrigger = safeValue.slice(mentionTriggerIndex);
      const shouldClose = mentionMode === 'types'
        ? safeCursorPos <= mentionTriggerIndex || /\s/.test(textAfterTrigger.slice(1)) || !safeValue.includes('@')
        : safeCursorPos <= mentionTriggerIndex || !safeValue.includes('@');

      if (shouldClose) {
        closeMentionPopover();
      }
    }

    updateMessage(safeValue);
  };

  const closeMentionPopover = () => {
    setMentionOpen(false);
    setMentionTriggerIndex(-1);
    setHighlightedIndex(0);
    setMentionMode('types');
  };

  const handleTypeSelect = (typeId) => {
    const beforeTrigger = message.slice(0, mentionTriggerIndex);
    const afterQuery = message.slice(mentionTriggerIndex + mentionQuery.length + 1);

    if (typeId === 'policy') {
      const newMessage = `${beforeTrigger}@policy ${afterQuery}`;
      updateMessage(newMessage);
      setMentionMode('policies');
      setHighlightedIndex(0);
      onLoadPolicies();
    } else if (typeId === 'tasks') {
      const newMessage = `${beforeTrigger}@tasks ${afterQuery}`;
      updateMessage(newMessage);
      setMentionMode('tasks');
      setHighlightedIndex(0);
      onLoadTasks();
    } else if (typeId === 'documents') {
      const newMessage = `${beforeTrigger}@documents ${afterQuery}`;
      updateMessage(newMessage);
      setMentionMode('documents');
      setHighlightedIndex(0);
      onLoadDocuments();
    } else if (typeId === 'functions') {
      const newMessage = `${beforeTrigger}@functions ${afterQuery}`;
      updateMessage(newMessage);
      setMentionMode('functions');
      setHighlightedIndex(0);
      onLoadFunctions();
    } else if (typeId === 'subscribers') {
      const newMessage = `${beforeTrigger}@subscribers ${afterQuery}`;
      updateMessage(newMessage);
      setMentionMode('subscribers');
      setHighlightedIndex(0);
      onLoadSubscribers();
    }
  };

  const handlePolicySelect = (mention) => {
    const beforeTrigger = message.slice(0, mentionTriggerIndex);
    const afterQuery = message.slice(mentionTriggerIndex + mentionQuery.length + 1);
    const newMessage = `${beforeTrigger}${mention} ${afterQuery}`;
    updateMessage(newMessage);
    closeMentionPopover();

    // Open policy in sidebar — extract ID from @policy:ID
    const policyId = mention.replace('@policy:', '');
    if (policyId) onOpenPolicy(policyId);
  };

  const handleTaskSelect = (mention) => {
    const beforeTrigger = message.slice(0, mentionTriggerIndex);
    const afterQuery = message.slice(mentionTriggerIndex + mentionQuery.length + 1);
    const newMessage = `${beforeTrigger}${mention} ${afterQuery}`;
    updateMessage(newMessage);
    closeMentionPopover();

    const taskId = mention.replace('@task:', '');
    if (taskId) onOpenTask(taskId);
  };

  const handleDocumentSelect = (doc) => {
    const beforeTrigger = message.slice(0, mentionTriggerIndex);
    const afterQuery = message.slice(mentionTriggerIndex + mentionQuery.length + 1);

    // Normalize the path - ensure it has user_uploads/ prefix for the mention
    // The storage API returns paths that may or may not include the prefix
    const filePath = doc.path.startsWith('user_uploads/') ? doc.path : `user_uploads/${doc.path}`;
    const mention = `@document:${filePath}`;
    const newMessage = `${beforeTrigger}${mention} ${afterQuery}`;
    updateMessage(newMessage);
    closeMentionPopover();

    // Open document in viewer drawer
    if (onOpenDocument) {
      const displayName = getDocumentDisplayName(doc.displayName || doc.name || filePath)
        || doc.name
        || filePath.split('/').pop()
        || 'Document';
      console.log('Opening document:', { originalPath: doc.path, normalizedPath: filePath, name: displayName });
      onOpenDocument({
        id: doc.id || filePath,
        name: displayName,
        path: filePath,
        type: displayName?.split('.').pop()?.toLowerCase(),
      });
    }
  };

  const handleFunctionSelect = (mention) => {
    const beforeTrigger = message.slice(0, mentionTriggerIndex);
    const afterQuery = message.slice(mentionTriggerIndex + mentionQuery.length + 1);
    const newMessage = `${beforeTrigger}${mention} ${afterQuery}`;
    updateMessage(newMessage);
    closeMentionPopover();
  };

  const handleSubscriberSelect = (mention) => {
    const beforeTrigger = message.slice(0, mentionTriggerIndex);
    const afterQuery = message.slice(mentionTriggerIndex + mentionQuery.length + 1);
    const newMessage = `${beforeTrigger}${mention} ${afterQuery}`;
    updateMessage(newMessage);
    closeMentionPopover();
  };

  const handleFile = useCallback(async (selected) => {
    if (!selected) return;

    if (selected.type !== 'application/pdf') {
      toast.error('Only PDF files are supported.');
      return;
    }

    if (selected.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.error(`File must be smaller than ${MAX_FILE_SIZE_MB} MB.`);
      return;
    }

    if (isUploading) return;

    setIsUploading(true);
    setUploadingFileName(selected.name);

    try {
      const uploadKey = await resolveUploadKey(selected.name);
      await uploadFile(selected, uploadKey);

      const normalizedPath = uploadKey.startsWith('user_uploads/')
        ? uploadKey
        : `user_uploads/${uploadKey}`;
      const resolvedName = uploadKey.split('/').pop() || selected.name;
      const displayName = getDocumentDisplayName(selected.name || resolvedName)
        || selected.name
        || resolvedName;

      insertDocumentMentionAtCursor(normalizedPath);

      if (onOpenDocument) {
        onOpenDocument({
          id: normalizedPath,
          name: displayName,
          path: normalizedPath,
          type: 'pdf',
        });
      }

      await onLoadDocuments();
      toast.success(`Uploaded ${displayName}`);
    } catch (error) {
      console.error('Failed to upload chat document:', error);
      toast.error(error?.message || `Failed to upload ${selected.name}`);
    } finally {
      setIsUploading(false);
      setUploadingFileName('');
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 0);
    }
  }, [
    insertDocumentMentionAtCursor,
    isUploading,
    onLoadDocuments,
    onOpenDocument,
    resolveUploadKey,
    textareaRef,
  ]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files?.[0];
    handleFile(droppedFile);
  }, [handleFile]);

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e) => {
    const selected = e.target.files?.[0];
    handleFile(selected);
    e.target.value = '';
  };

  const handleSuggestedPrompt = (prompt) => {
    updateMessage(prompt);
  };

  const showWelcome = isCreationMode || (!messages?.length && !busy);

  const groupedMessages = useMemo(() => {
    if (!messages) return [];
    const grouped = [];
    let i = 0;
    while (i < messages.length) {
      const msg = messages[i];
      if (msg.role === 'tool_call' || msg.role === 'tool_output') {
        const group = [];
        while (i < messages.length && (messages[i].role === 'tool_call' || messages[i].role === 'tool_output')) {
          group.push(messages[i]);
          i++;
        }
        grouped.push({ type: 'tool_group', msgs: group, key: `group-${group[0].id || group[0].timestamp || i}` });
      } else {
        grouped.push({ type: 'single', msg, key: `${msg.id || msg.timestamp || i}-${msg.role}-${i}` });
        i++;
      }
    }
    return grouped;
  }, [messages]);

  return (
    <Box
      sx={{ flex: 1, display: 'flex', flexDirection: 'row', minHeight: 0, overflow: 'hidden' }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
        minWidth: 0,
        position: 'relative',
        ...(isDragging && {
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            bgcolor: 'primary.softBg',
            opacity: 0.9,
            zIndex: 10,
            border: '2px dashed',
            borderColor: 'primary.500',
            borderRadius: 'lg',
            m: 2,
          },
        }),
      }}
    >
      {/* Header with cancel button in creation mode */}
      {isCreationMode && (
        <Box
          sx={{
            px: 3,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography level="title-md">New Conversation</Typography>
        </Box>
      )}

      {/* Delete button for active chat */}
      {!isCreationMode && onDeleteChat && (
        <Box
          sx={{
            px: 2,
            py: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Tooltip title="Delete conversation">
            <IconButton
              size="sm"
              variant="plain"
              color="danger"
              onClick={onDeleteChat}
            >
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Messages Area with centered content */}
      <Box
        className="agents-messages-container"
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isDragging ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              flex: 1,
              color: 'primary.500',
              zIndex: 11,
              position: 'relative',
            }}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 48, mb: 2 }} />
            <Typography level="h4">Drop PDF here</Typography>
          </Stack>
        ) : showWelcome ? (
          <WelcomeScreen
            isCreationMode={isCreationMode}
            onSelectPrompt={handleSuggestedPrompt}
          />
        ) : (
          <Box
            sx={{
              flex: 1,
              py: 3,
              px: 2,
            }}
          >
            <Box sx={{ maxWidth: MAX_CHAT_WIDTH, mx: 'auto' }}>
              <AnimatePresence initial={false}>
                {groupedMessages.map((item) => (
                  <motion.div
                    key={item.key}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                    style={{ marginBottom: '24px' }}
                  >
                    {item.type === 'tool_group' && (
                      <ToolCallGroup messages={item.msgs} />
                    )}
                    {item.type === 'single' && item.msg.role === 'user' && (
                      <UserMessage
                        message={item.msg}
                        onQuote={onQuote}
                        onOpenDocument={onOpenDocument}
                        onOpenPolicy={onOpenPolicy}
                        onOpenTask={onOpenTask}
                      />
                    )}
                    {item.type === 'single' && item.msg.role === 'assistant' && (
                      <AssistantMessage
                        message={item.msg}
                        agentId={agentId}
                        onQuote={onQuote}
                        onOpenDocument={onOpenDocument}
                        onOpenPolicy={onOpenPolicy}
                        onOpenTask={onOpenTask}
                      />
                    )}
                  </motion.div>
                ))}

                {/* Typing Indicator when agent is busy */}
                {busy && (
                  <TypingIndicator message="Agent is thinking" />
                )}
              </AnimatePresence>
              <div ref={messagesEndRef} />
            </Box>
          </Box>
        )}
      </Box>

      {/* Floating Input Area */}
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          px: 2,
          py: 2,
          background: (theme) =>
            `linear-gradient(to top, ${theme.vars.palette.background.surface} 80%, transparent)`,
        }}
      >
        <Box
          sx={{
            maxWidth: MAX_CHAT_WIDTH,
            mx: 'auto',
          }}
        >
          {/* Document Crop Attachment */}
          <AnimatePresence>
            {documentCropAttachment && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  sx={{
                    mb: 1,
                    px: 2,
                    py: 1,
                    bgcolor: 'success.softBg',
                    borderRadius: 'md',
                    border: '1px solid',
                    borderColor: 'success.outlinedBorder',
                  }}
                >
                  <CropOriginalIcon sx={{ fontSize: 18, color: 'success.600' }} />
                  {documentCropAttachment.dataUrl && (
                    <Box
                      component="img"
                      src={documentCropAttachment.dataUrl}
                      alt="Crop preview"
                      sx={{
                        height: 40,
                        maxWidth: 80,
                        objectFit: 'contain',
                        borderRadius: 'sm',
                        border: '1px solid',
                        borderColor: 'neutral.outlinedBorder',
                      }}
                    />
                  )}
                  <Stack sx={{ flex: 1 }}>
                    <Typography level="body-sm">
                      Region from {documentCropAttachment.documentName}
                    </Typography>
                    {documentCropAttachment.page && (
                      <Typography level="body-xs" color="neutral">
                        Page {documentCropAttachment.page}
                      </Typography>
                    )}
                  </Stack>
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={onRemoveCropAttachment}
                    sx={{ '--IconButton-size': '24px' }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Stack>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Input Container */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 1,
              bgcolor: 'background.surface',
              borderRadius: 'xl',
              border: '1px solid',
              borderColor: 'neutral.outlinedBorder',
              boxShadow: 'md',
              p: 1,
              transition: 'all 0.2s ease',
              '&:focus-within': {
                borderColor: 'primary.500',
                boxShadow: (theme) =>
                  `0 0 0 3px ${theme.vars.palette.primary.outlinedBorder}`,
              },
            }}
          >
            <input
              type="file"
              accept="application/pdf"
              ref={fileInputRef}
              onChange={handleFileInputChange}
              disabled={busy || sending || isUploading}
              style={{ display: 'none' }}
            />

            <IconButton
              variant="plain"
              color="neutral"
              onClick={handleAttachClick}
              disabled={busy || sending || isUploading}
              title="Attach PDF"
              sx={{ flexShrink: 0 }}
            >
              <AttachFileIcon />
            </IconButton>

            <Box sx={{ flex: 1, position: 'relative' }}>
              <RichChatInput
                inputRef={textareaRef}
                cursorPositionRef={cursorPositionRef}
                minRows={1}
                maxRows={6}
                placeholder={
                  busy
                    ? 'Agent is thinking…'
                    : isCreationMode
                      ? 'Describe what you want to extract from documents…'
                      : 'Message Agent…'
                }
                value={message}
                onChange={handleTextChange}
                onKeyDown={handleKeyDown}
                disabled={busy}
                quotes={quotes}
                onRemoveQuote={onRemoveQuote}
                onOpenDocument={onOpenDocument}
                onOpenPolicy={onOpenPolicy}
              />
              <PolicyMentionPopover
                open={mentionOpen}
                anchorEl={textareaRef.current}
                query={mentionQuery}
                mode={mentionMode}
                policies={policies}
                loading={policiesLoading}
                tasks={tasks}
                tasksLoading={tasksLoading}
                hasMoreTasks={hasMoreTasks}
                documents={documents}
                documentFolders={documentFolders}
                documentsLoading={documentsLoading}
                documentBreadcrumbs={documentBreadcrumbs}
                onNavigateFolder={onNavigateDocumentFolder}
                onNavigateDocumentRoot={onNavigateDocumentRoot}
                onSelectDocument={handleDocumentSelect}
                functions={functions}
                functionsLoading={functionsLoading}
                onSelectFunction={handleFunctionSelect}
                subscribers={subscribers}
                subscribersLoading={subscribersLoading}
                onSelectSubscriber={handleSubscriberSelect}
                onSelectType={handleTypeSelect}
                onSelectPolicy={handlePolicySelect}
                onSelectTask={handleTaskSelect}
                onLoadMoreTasks={onLoadMoreTasks}
                onClose={closeMentionPopover}
                highlightedIndex={highlightedIndex}
              />
            </Box>

            <IconButton
              variant="solid"
              color="primary"
              onClick={onSend}
              loading={sending}
              disabled={busy || sending || isUploading || !message.trim()}
              sx={{
                flexShrink: 0,
                borderRadius: 'lg',
                '--IconButton-size': '40px',
              }}
            >
              <SendRoundedIcon />
            </IconButton>
          </Box>

          {/* Status Messages */}
          <AnimatePresence>
            {(isUploading || busy) && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {isUploading && (
                  <Typography
                    level="body-xs"
                    color="primary"
                    sx={{ mt: 1, textAlign: 'center' }}
                  >
                    Uploading {uploadingFileName || 'document'}…
                  </Typography>
                )}
                {busy && (
                  <Typography
                    level="body-xs"
                    color="warning"
                    sx={{ mt: 1, textAlign: 'center' }}
                  >
                    Agent is processing your request…
                  </Typography>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Model and Mode Selectors */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={0}
            divider={<Divider orientation="vertical" sx={{ mx: 0.5 }} />}
          >
            <ModeSelector
              modes={modes}
              selectedMode={selectedMode}
              onSelect={onModeChange}
              loading={modesLoading}
              disabled={busy}
            />
            <ModelSelector
              models={models}
              selectedModelId={selectedModelId}
              onSelect={onModelChange}
              loading={modelsLoading}
              disabled={busy}
            />
          </Stack>
        </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Chat;
